/**
 * Marketing team management.
 *
 * Replaces `renderTeamManagement`, `filterTeamByRole`, `openAddTeamMemberModal`,
 * `editTeamMember`, `saveTeamMember`, `deleteTeamMember`,
 * `toggleTeamMemberProjects`, `openMemberPortal`, `toggleMemberCapacityStatus`
 * and `openModeratorModal` / `confirmModAssignment` / `updateDoctorModerator`
 * from app.js.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Spinner,
  StatCard,
} from '../../components/common';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { teamApi } from '../../api/endpoints';
import { validateTeamMember } from '../../domain/validation';

/** Job labels, from the legacy role filter chips. */
const JOB_OPTIONS = [
  { value: 'designer', label: 'مصمم' },
  { value: 'editor', label: 'مونتير' },
  { value: 'photographer', label: 'مصور' },
  { value: 'copywriter', label: 'كاتب محتوى' },
  { value: 'moderator', label: 'مودريتور' },
  { value: 'media_buyer', label: 'ميديا باير' },
];

const jobLabel = (value) => JOB_OPTIONS.find((j) => j.value === value)?.label || value;

const emptyMember = () => ({
  name: '',
  username: '',
  password: '',
  contact: '',
  team_member_code: '',
  roles: [],
  capacityStatus: 'available',
  status: 'active',
});

export function AdminTeam() {
  const { teamMembers, teamMembersState, loadTeamMembers, doctors } = useData();
  const { success, error: toastError } = useNotifications();

  const [roleFilter, setRoleFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(
    () =>
      roleFilter
        ? teamMembers.filter((m) => (m.roles || []).includes(roleFilter))
        : teamMembers,
    [teamMembers, roleFilter],
  );

  const totals = useMemo(
    () => ({
      total: teamMembers.length,
      available: teamMembers.filter((m) => m.capacityStatus !== 'full').length,
      full: teamMembers.filter((m) => m.capacityStatus === 'full').length,
      projects: teamMembers.reduce((sum, m) => sum + (m.assignedProjects?.length || 0), 0),
    }),
    [teamMembers],
  );

  const save = async (member) => {
    setBusy(true);
    try {
      const payload = {
        name: member.name.trim(),
        username: member.username.trim(),
        contact: member.contact?.trim() || null,
        team_member_code: member.team_member_code?.trim() || null,
        roles: member.roles,
        capacityStatus: member.capacityStatus,
        status: member.status,
        ...(member.password ? { password: member.password } : {}),
      };

      if (member.id) {
        await teamApi.updateMember(member.id, payload);
        success('تم تحديث بيانات العضو');
      } else {
        await teamApi.createMember(payload);
        success('تمت إضافة العضو');
      }

      setEditing(null);
      loadTeamMembers();
    } catch (err) {
      toastError(err.message || 'تعذر حفظ بيانات العضو');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await teamApi.deleteMember(deleteTarget.id);
      success('تم حذف العضو');
      loadTeamMembers();
    } catch (err) {
      toastError(err.message || 'تعذر حذف العضو');
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const toggleCapacity = async (member) => {
    setBusy(true);
    try {
      await teamApi.updateMember(member.id, {
        capacityStatus: member.capacityStatus === 'full' ? 'available' : 'full',
      });
      loadTeamMembers();
    } catch (err) {
      toastError(err.message || 'تعذر تغيير حالة الإتاحة');
    } finally {
      setBusy(false);
    }
  };

  const assignDoctor = async (doctorId) => {
    if (!assignTarget || !doctorId) return;
    setBusy(true);
    try {
      await teamApi.assignDoctor({ teamMemberId: assignTarget.id, doctorId: Number(doctorId) });
      success('تم إسناد الطبيب للمودريتور');
      setAssignTarget(null);
      loadTeamMembers();
    } catch (err) {
      toastError(err.message || 'تعذر إسناد الطبيب');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="فريق العمل"
        subtitle={`${teamMembers.length} عضو`}
        icon="users"
        actions={
          <Button icon="user-plus" onClick={() => setEditing(emptyMember())}>
            إضافة عضو
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="users" label="إجمالي الأعضاء" value={totals.total} accent="cyan" />
        <StatCard icon="user-check" label="متاح" value={totals.available} accent="emerald" />
        <StatCard icon="user-x" label="مشغول" value={totals.full} accent="amber" />
        <StatCard icon="briefcase" label="مشاريع مسندة" value={totals.projects} accent="purple" />
      </div>

      <Panel>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setRoleFilter('')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
              roleFilter === ''
                ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
            }`}
          >
            الكل
          </button>
          {JOB_OPTIONS.map((job) => (
            <button
              key={job.value}
              type="button"
              onClick={() => setRoleFilter(job.value)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                roleFilter === job.value
                  ? 'border-cyan-500 bg-cyan-500/15 text-cyan-300'
                  : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
              }`}
            >
              {job.label}
            </button>
          ))}
        </div>
      </Panel>

      {teamMembersState.error ? (
        <ErrorState error={teamMembersState.error} onRetry={loadTeamMembers} />
      ) : teamMembersState.loading && teamMembers.length === 0 ? (
        <Spinner label="جاري تحميل الفريق..." />
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState
            icon="users"
            title="لا يوجد أعضاء"
            message={roleFilter ? 'لا يوجد أعضاء بهذا التخصص.' : 'أضف أول عضو في فريق التسويق.'}
          />
        </Panel>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              doctors={doctors}
              busy={busy}
              onEdit={() => setEditing({ ...emptyMember(), ...member, password: '' })}
              onDelete={() => setDeleteTarget(member)}
              onToggleCapacity={() => toggleCapacity(member)}
              onAssignDoctor={() => setAssignTarget(member)}
            />
          ))}
        </div>
      )}

      <MemberModal member={editing} busy={busy} onClose={() => setEditing(null)} onSave={save} />

      {/* Moderator -> doctor assignment */}
      <Modal
        open={Boolean(assignTarget)}
        onClose={() => setAssignTarget(null)}
        title="إسناد طبيب للمودريتور"
        icon="user-plus"
        size="sm"
      >
        {assignTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              اختر طبيباً ليتولى <strong className="text-white">{assignTarget.name}</strong> إدارة
              صفحاته.
            </p>

            <Field label="الطبيب">
              <Select
                defaultValue=""
                onChange={(event) => assignDoctor(event.target.value)}
                disabled={busy}
              >
                <option value="">اختر طبيباً</option>
                {doctors
                  .filter((d) => !(assignTarget.assignedDoctors || []).includes(d.id))
                  .map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
              </Select>
            </Field>

            {(assignTarget.assignedDoctors || []).length > 0 && (
              <div>
                <p className="mb-2 text-xs font-bold text-slate-300">الأطباء الحاليون</p>
                <div className="flex flex-wrap gap-1.5">
                  {assignTarget.assignedDoctors.map((id) => {
                    const doctor = doctors.find((d) => d.id === id);
                    return (
                      <Badge
                        key={id}
                        className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300"
                        icon="stethoscope"
                      >
                        {doctor?.name || `طبيب #${id}`}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title="حذف عضو الفريق"
        confirmLabel="حذف"
        message={`سيتم حذف حساب ${deleteTarget?.name || 'العضو'} نهائياً ولن يتمكن من تسجيل الدخول.`}
      />
    </div>
  );
}

/* ========================================================================== */

function MemberCard({ member, doctors, busy, onEdit, onDelete, onToggleCapacity, onAssignDoctor }) {
  const isModerator = (member.roles || []).includes('moderator');
  const projects = member.assignedProjects || [];

  return (
    <Panel className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <Avatar name={member.name} size={44} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-bold text-white">{member.name}</h3>
          <p className="truncate text-xs text-slate-400" dir="ltr">
            @{member.username}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge
              className={
                member.capacityStatus === 'full'
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                  : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
              }
            >
              {member.capacityStatus === 'full' ? 'مشغول' : 'متاح'}
            </Badge>
            {member.team_member_code && (
              <Badge className="border-slate-600 bg-slate-800 text-slate-300" icon="hash">
                {member.team_member_code}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {(member.roles || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {member.roles.map((role) => (
            <span
              key={role}
              className="rounded bg-purple-500/10 px-2 py-0.5 text-[10px] text-purple-300"
            >
              {jobLabel(role)}
            </span>
          ))}
        </div>
      )}

      {projects.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
            {projects.length} مشروع مسند
          </summary>
          <ul className="mt-2 space-y-1">
            {projects.map((project) => (
              <li key={project.id} className="flex justify-between text-[11px] text-slate-500">
                <span>{project.doctorName}</span>
                <span>{project.status}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {isModerator && (member.assignedDoctors || []).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {member.assignedDoctors.map((id) => (
            <Badge key={id} className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="stethoscope">
              {doctors.find((d) => d.id === id)?.name || `#${id}`}
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-auto flex flex-wrap justify-end gap-1.5 border-t border-white/5 pt-3">
        {isModerator && (
          <Button variant="subtle" icon="user-plus" onClick={onAssignDoctor}>
            إسناد طبيب
          </Button>
        )}
        <Button
          variant="subtle"
          icon={member.capacityStatus === 'full' ? 'play' : 'pause'}
          loading={busy}
          onClick={onToggleCapacity}
        >
          {member.capacityStatus === 'full' ? 'إتاحة' : 'تعليم كمشغول'}
        </Button>
        <Button variant="subtle" icon="pencil" onClick={onEdit}>
          تعديل
        </Button>
        <Button variant="subtle" icon="trash-2" onClick={onDelete} className="!text-red-300">
          حذف
        </Button>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function MemberModal({ member, busy, onClose, onSave }) {
  const [form, setForm] = useState(member);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(member);
    setErrors({});
  }, [member]);

  if (!form) return null;

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const toggleRole = (role) =>
    setForm((current) => {
      const roles = current.roles || [];
      return {
        ...current,
        roles: roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role],
      };
    });

  const handleSubmit = () => {
    const validation = validateTeamMember({ ...form, isNew: !form.id });
    setErrors(validation.errors);
    if (!validation.valid) return;
    onSave(form);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? 'تعديل عضو الفريق' : 'إضافة عضو'}
      icon="user-plus"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button icon="save" loading={busy} onClick={handleSubmit}>
            حفظ
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="الاسم" error={errors.name} required>
          <Input value={form.name} onChange={update('name')} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="اسم المستخدم" error={errors.username} required>
            <Input value={form.username} onChange={update('username')} dir="ltr" />
          </Field>

          <Field
            label="كلمة المرور"
            error={errors.password}
            required={!form.id}
            hint={form.id ? 'اتركها فارغة للإبقاء على الحالية' : undefined}
          >
            <Input
              type="password"
              value={form.password || ''}
              onChange={update('password')}
              autoComplete="new-password"
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="رقم التواصل">
            <Input value={form.contact || ''} onChange={update('contact')} dir="ltr" />
          </Field>

          <Field label="كود العضو" hint="يُستخدم للربط مع المشاريع">
            <Input value={form.team_member_code || ''} onChange={update('team_member_code')} dir="ltr" />
          </Field>
        </div>

        <Field label="التخصصات">
          <div className="flex flex-wrap gap-2">
            {JOB_OPTIONS.map((job) => {
              const active = (form.roles || []).includes(job.value);
              return (
                <button
                  key={job.value}
                  type="button"
                  onClick={() => toggleRole(job.value)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                    active
                      ? 'border-purple-500 bg-purple-500/15 text-purple-300'
                      : 'border-white/10 bg-slate-900 text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {job.label}
                </button>
              );
            })}
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="حالة الإتاحة">
            <Select value={form.capacityStatus} onChange={update('capacityStatus')}>
              <option value="available">متاح</option>
              <option value="full">مشغول</option>
            </Select>
          </Field>

          <Field label="حالة الحساب">
            <Select value={form.status} onChange={update('status')}>
              <option value="active">نشط</option>
              <option value="suspended">موقوف</option>
            </Select>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

export default AdminTeam;
